<template>
  <div
    class="executive-summary-allow-block-list pa-6"
    :class="`executive-summary-allow-block-list--${listType}`"
    data-testid="executive-summary-allow-block-list"
  >
    <div class="subtitle1 mb-4" data-testid="executive-summary-allow-block-title">
      {{ title }}
    </div>
    <div v-for="row in rows" :key="row.testId">
      <div class="d-flex justify-space-between py-2">
        <span class="body2">{{ row.label }}</span>
        <span class="body2" :data-testid="row.testId">{{ formatReportNumber(row.value) }}</span>
      </div>
      <v-divider class="border-opacity-100" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { ExecutiveSummaryEmailListType } from "@/constants/reports";
import { formatReportNumber } from "@/_helpers/reports";
import type { ExecutiveSummaryEmailListCounts } from "@/_store/reports.module";

export default defineComponent({
  props: {
    listType: {
      type: String as PropType<ExecutiveSummaryEmailListType>,
      required: true,
    },
    counts: {
      type: Object as PropType<ExecutiveSummaryEmailListCounts>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const title = computed(() =>
      props.listType === ExecutiveSummaryEmailListType.ALLOWLIST
        ? i18n.t("reports.executiveSummaryReport.addedToAllowlist")
        : i18n.t("reports.executiveSummaryReport.addedToBlocklist")
    );

    const rows = computed(() => [
      {
        testId: "executive-summary-allow-block-emails",
        label: i18n.t("reports.executiveSummaryReport.emailAddresses"),
        value: props.counts.emails,
      },
      {
        testId: "executive-summary-allow-block-domains",
        label: i18n.t("reports.executiveSummaryReport.domains"),
        value: props.counts.domains,
      },
      {
        testId: "executive-summary-allow-block-ips",
        label: i18n.t("reports.executiveSummaryReport.ipAddresses"),
        value: props.counts.ips,
      },
    ]);

    return { title, rows, formatReportNumber };
  },
});
</script>

<style scoped lang="scss">
.executive-summary-allow-block-list {
  border-radius: 8px;
  border: 1px solid;

  // Each card is built from ONE hue: the border at full strength, the fill the same token at
  // 35%, and the dividers that token at full strength. Measured off the mock frame on PLAT-4231
  // (#46a762 / #f6fbf2 / #e6f4d8 and #f74b4b / #fcf6f6 / #f5e5e5) — the fill is the only value
  // with no token of its own, and 35% reproduces both cards to within one channel step.
  &--allowlist {
    border-color: rgb(var(--v-theme-green-base));
    background: rgba(var(--v-theme-green-faint), 0.35);

    .v-divider {
      border-color: rgb(var(--v-theme-green-faint));
    }
  }

  &--blocklist {
    border-color: rgb(var(--v-theme-red-base));
    background: rgba(var(--v-theme-red-faint), 0.35);

    .v-divider {
      border-color: rgb(var(--v-theme-red-faint));
    }

    .subtitle1 {
      color: rgb(var(--v-theme-red-dark));
    }
  }
}
</style>
